// Demo employees, teams, statistics, DEI, cohort data
import { demoData, getEmployeeAnalysis, getEmployeeDetails } from './data';

// 29-employee evaluation grade mapping (S:3, A:6, B:12, C:6, D:2 — forced distribution)
export const EVAL_GRADES: Record<string, { grade: string; score: number; status: string }> = {
  TS2600001: { grade: 'S', score: 47, status: 'completed' }, TS2600002: { grade: 'B', score: 38, status: 'completed' },
  TS2600003: { grade: 'C', score: 30, status: 'completed' }, TS2600004: { grade: 'A', score: 44, status: 'completed' },
  TS2600005: { grade: 'B', score: 39, status: 'completed' }, TS2600006: { grade: 'B', score: 37, status: 'completed' },
  TS2600007: { grade: 'C', score: 29, status: 'in_progress' }, TS2600008: { grade: 'C', score: 28, status: 'in_progress' },
  TS2600009: { grade: 'S', score: 48, status: 'completed' }, TS2600010: { grade: 'A', score: 43, status: 'completed' },
  TS2600011: { grade: 'B', score: 38, status: 'completed' }, TS2600012: { grade: 'B', score: 36, status: 'completed' },
  TS2600013: { grade: 'C', score: 31, status: 'completed' }, TS2600014: { grade: 'B', score: 35, status: 'completed' },
  TS2600015: { grade: 'S', score: 46, status: 'completed' }, TS2600016: { grade: 'A', score: 42, status: 'completed' },
  TS2600017: { grade: 'B', score: 37, status: 'completed' }, TS2600018: { grade: 'C', score: 30, status: 'pending' },
  TS2600019: { grade: 'A', score: 45, status: 'completed' }, TS2600020: { grade: 'A', score: 43, status: 'completed' },
  TS2600021: { grade: 'B', score: 36, status: 'completed' }, TS2600022: { grade: 'C', score: 29, status: 'completed' },
  TS2600023: { grade: 'D', score: 24, status: 'pending' }, TS2600024: { grade: 'A', score: 44, status: 'completed' },
  TS2600025: { grade: 'B', score: 38, status: 'completed' }, TS2600026: { grade: 'B', score: 37, status: 'completed' },
  TS2600027: { grade: 'B', score: 36, status: 'completed' }, TS2600028: { grade: 'B', score: 35, status: 'in_progress' },
  TS2600029: { grade: 'D', score: 22, status: 'pending' },
};

// Team radar chart axes + per-employee score generator
const TEAM_AXES: Record<string, string[]> = {
  '경영지원팀': ['조직관리', '의사소통', '문서관리', '규정준수', '분석력'],
  '영업팀': ['고객관리', '매출달성', '협상력', '시장분석', '팀워크'],
  '연구개발팀': ['기술역량', '문제해결', '혁신성', '일정관리', '팀워크'],
  '품질관리팀': ['품질분석', '프로세스관리', '문서화', '규정준수', '분석력'],
  '생산팀': ['현장관리', '안전관리', '설비운용', '인력관리', '생산성'],
  'IT개발팀': ['아키텍처', '코드품질', '문제해결', '기술트렌드', '협업'],
};

export const genRadar = (empId: string, team: string) => {
  const axes = TEAM_AXES[team] || ['업무전문성', '문제해결', '소통', '자기개발', '팀워크'];
  const g = EVAL_GRADES[empId];
  const base = g ? g.score / 50 * 5 : 3.5;
  const seed = parseInt(empId.slice(-5), 10);
  return axes.map((axis, i) => ({
    axis, score: Math.round((base + ((seed * (i + 3) * 7) % 10 - 5) * 0.12) * 10) / 10, max_score: 5,
  }));
};

export const demoEmployeesService = {
  // ========== Auth / access helpers ==========
  checkPiiBypass: async () => ({ bypass: false }),

  // ========== Employees ==========
  getAllEmployees: async () => demoData.employees ?? { employees: [], total: 0 },
  getEmployee: async (id: string) => {
    const emp: any = demoData.employees?.employees?.find((e: any) => e.employee_id === id || e.id === id);
    if (!emp) return null;
    const analysisData = getEmployeeAnalysis(id);
    const details = getEmployeeDetails(id);
    const birthYear = 2026 - emp.age;
    const monthDay = ['03-15', '07-22', '11-08', '01-25', '05-30', '09-12', '02-18', '06-03', '10-27', '04-09'];
    const md = monthDay[(parseInt(id.slice(-5), 10) - 1) % monthDay.length];
    return {
      ...emp,
      id: emp.employee_id || id,
      name: emp.name,
      join_date: emp.hire_date || emp.join_date,
      birth_date: `${birthYear}-${md}`,
      contact: `010-${String(1000 + parseInt(id.slice(-5), 10) * 37 % 9000).padStart(4, '0')}-${String(1000 + parseInt(id.slice(-5), 10) * 71 % 9000).padStart(4, '0')}`,
      email: `${(emp.employee_id || id).toLowerCase()}@example.com`,
      address: emp.region || undefined,
      salary: Math.round(emp.salary / 10000),
      education: details.education,
      career: details.career,
      certifications: details.certifications,
      company_business_number: '123-45-67890',
      company_representative: '김대표',
      ...analysisData,
    };
  },
  updateEmployee: async () => ({ message: 'Demo mode', employee: null }),
  getEmployeeEvaluations: async () => ({ employee_id: '', evaluations: [], total: 0 }),
  addEmployeeEvaluation: async () => ({ message: 'Demo mode', evaluation_id: 0, reanalyzed: false }),
  deleteEmployeeEvaluation: async () => ({ message: 'Demo mode', deleted_id: 0 }),
  getEmployeePersonnelChanges: async (id: string) => {
    const changes = demoData.personnelChanges?.changes?.filter((c: any) => c.employee_id === id) || [];
    return { employee_id: id, changes, total: changes.length };
  },

  // ========== Teams ==========
  getTeams: async () => {
    const base = demoData.teams ?? { teams: [] };
    // org_hierarchy for Split-View Aggregate tree (matches demoWeeklyReports aggregate IDs)
    const org_hierarchy = [
      { name: 'TSCORP', code: 'C001', org_unit_id: 100, unit_type: 'company', parent_code: null, member_count: 62 },
      { name: '개발본부', code: 'D001', org_unit_id: 1, unit_type: 'division', parent_code: 'C001', member_count: 24 },
      { name: '사업개발본부', code: 'D002', org_unit_id: 2, unit_type: 'division', parent_code: 'C001', member_count: 18 },
      { name: '경영지원본부', code: 'D003', org_unit_id: 4, unit_type: 'division', parent_code: 'C001', member_count: 20 },
      { name: 'AI Lab', code: 'T001', org_unit_id: 3, unit_type: 'team', parent_code: 'D001', member_count: 8 },
      { name: '플랫폼팀', code: 'T002', org_unit_id: 5, unit_type: 'team', parent_code: 'D001', member_count: 9 },
      { name: '인프라팀', code: 'T003', org_unit_id: 6, unit_type: 'team', parent_code: 'D001', member_count: 7 },
      { name: '영업팀', code: 'T004', org_unit_id: 7, unit_type: 'team', parent_code: 'D002', member_count: 10 },
      { name: '마케팅팀', code: 'T005', org_unit_id: 8, unit_type: 'team', parent_code: 'D002', member_count: 8 },
      { name: '인사팀', code: 'T006', org_unit_id: 9, unit_type: 'team', parent_code: 'D003', member_count: 6 },
      { name: '재무팀', code: 'T007', org_unit_id: 10, unit_type: 'team', parent_code: 'D003', member_count: 7 },
      { name: '경영지원', code: 'T008', org_unit_id: 11, unit_type: 'team', parent_code: 'D003', member_count: 7 },
    ];
    return { ...(base as any), org_hierarchy, teams_with_codes: org_hierarchy };
  },
  getTeamEmployees: async (team: string) => {
    const emps = demoData.employees?.employees?.filter((e: any) => e.team === team) || [];
    return { team, employees: emps, total: emps.length };
  },
  getTeamAnalysis: async (team: string) => {
    const emps = demoData.employees?.employees?.filter((e: any) => e.team === team) || [];
    const avgAge = emps.length ? Math.round(emps.reduce((s: number, e: any) => s + e.age, 0) / emps.length * 10) / 10 : 0;
    const avgTenure = emps.length ? Math.round(emps.reduce((s: number, e: any) => s + (e.years_of_service || 0), 0) / emps.length * 10) / 10 : 0;
    const teamProfiles: Record<string, any> = {
      '경영지원팀': {
        team_composition: `경영지원팀은 ${emps.length}명(부장 김도현, 대리 이서연, 사원 박준호)으로 구성된 소규모 핵심 지원 부서입니다. 평균 연령 ${avgAge}세, 평균 근속 ${avgTenure}년으로 경력 편차가 크나, 김도현 부장(14년차)의 리더십으로 안정적으로 운영되고 있습니다. 인사·총무·회계를 3명이 담당하여 업무 강도가 높은 편입니다.`,
        capability_gaps: { 'HR 데이터 분석': '인사 데이터 기반 의사결정 체계 미비, BI 도구 활용 역량 부족', '디지털 전환': 'ERP/그룹웨어 등 시스템 고도화를 위한 IT 활용 역량 보강 필요' },
        efficiency_issues: ['인사·총무·회계를 3명이 겸무하여 업무 과부하 상시 발생', '급여·4대보험 처리 시 수작업 비중이 높아 오류 위험 존재'],
        team_strengths: ['김도현 부장의 14년 경영관리 경험으로 안정적 운영', '이서연 대리의 빠른 업무 습득력과 멀티태스킹 능력', '전사 부서와의 원활한 커뮤니케이션 허브 역할'],
        critical_improvements: ['급여·인사 관련 업무 자동화 시스템 도입 시급', '박준호 사원의 조기 역량 개발을 위한 OJT 프로그램 필요'],
        hiring_needs: ['경영지원팀 인사/총무 담당 (경력 3~5년, 즉시 채용 필요)'],
      },
      '영업팀': {
        team_composition: `영업팀은 ${emps.length}명으로 팀장 최민지(12년차)를 중심으로 운영됩니다. 평균 연령 ${avgAge}세, 평균 근속 ${avgTenure}년이며, 과장 정우진의 초과근무(주 51.5시간)가 위험 수준입니다. 신입 한지수(1년차)와 윤성민(1.5년차)의 법정교육 미이수가 컴플라이언스 리스크로 식별됩니다.`,
        capability_gaps: { 'CRM 활용': '고객관계관리 시스템 활용도가 낮아 영업 파이프라인 가시성 부족', '데이터 기반 영업': '매출 예측 및 고객 분석을 위한 정량적 접근법 미흡' },
        efficiency_issues: ['정우진 과장 주간 51.5시간 근무로 52시간제 위반 임박', '강하은 대리 주간 49시간으로 초과근무 고위험', '신입 2명(윤성민, 한지수)의 법정교육 미이수 4건 잔존'],
        team_strengths: ['최민지 팀장의 12년 영업 경험과 고객 네트워크', '전년 대비 매출 12% 성장 달성', '팀 내 선후배 멘토링 문화 정착'],
        critical_improvements: ['정우진 과장의 초과근무 즉시 조정 (법적 리스크)', '윤성민·한지수 법정교육 2주 내 이수 완료 필요', '영업 활동 데이터 기록 체계화'],
        hiring_needs: ['영업팀 기술영업 담당 (경력 5년 이상, 산업기기 경험)', '영업팀 영업지원 (경력 1~3년, CRM 운영)'],
      },
      '연구개발팀': {
        team_composition: `연구개발팀은 ${emps.length}명으로 최대 규모 팀이며, 팀장 임태현(13년차)의 리더십 아래 핵심 기술 개발을 담당합니다. 평균 연령 ${avgAge}세, 평균 근속 ${avgTenure}년으로 시니어(임태현, 송예진)와 주니어(조현우, 신지은)의 균형이 양호합니다. 송예진 과장의 초과근무(주 47.5시간) 모니터링이 필요합니다.`,
        capability_gaps: { 'AI/ML 역량': '차세대 제품에 AI 적용이 요구되나 팀 내 전문 인력 부재', '클라우드 인프라': '온프레미스 중심 개발 환경에서 클라우드 전환 역량 부족' },
        efficiency_issues: ['송예진 과장 주간 47.5시간으로 초과근무 경고 수준', '코드 리뷰 프로세스 미정립으로 품질 편차 발생', '테스트 자동화 커버리지 40% 미만으로 릴리즈 지연 빈발'],
        team_strengths: ['임태현 팀장의 13년 R&D 경험과 특허 3건 보유', '오재혁·배수아 대리의 안정적 중간 관리 역할 수행', '신지은 사원의 빠른 적응력과 신기술 학습 의지'],
        critical_improvements: ['테스트 자동화 도입으로 릴리즈 품질 향상 시급', 'AI/ML 역량 확보를 위한 교육 또는 채용 병행 필요', '송예진 과장 업무 분산을 위한 인력 보강'],
        hiring_needs: ['연구개발팀 AI/ML 엔지니어 (경력 3년 이상)', '연구개발팀 QA 엔지니어 (테스트 자동화 경험)'],
      },
      '품질관리팀': {
        team_composition: `품질관리팀은 ${emps.length}명으로 팀장 서동훈(13년차)을 중심으로 제품 품질과 공정 관리를 담당합니다. 평균 연령 ${avgAge}세, 평균 근속 ${avgTenure}년이며, 전원 법정교육 이수 완료로 컴플라이언스 우수 팀입니다. 4명으로 업무 범위 대비 인력이 빠듯한 상황입니다.`,
        capability_gaps: { '통계적 품질관리(SQC)': 'SPC/MSA 등 통계 기법 활용이 제한적이어서 데이터 기반 품질 개선 한계', '글로벌 인증': 'ISO 13485, FDA 등 해외 인증 대응 역량 보강 필요' },
        efficiency_issues: ['품질 검사 보고서 수기 작성으로 데이터 활용 어려움', '불량률 추적이 수작업 기반으로 실시간 모니터링 불가'],
        team_strengths: ['법정교육 이수율 100%로 컴플라이언스 모범 팀', '서동훈 팀장의 13년 품질관리 전문성', '유지현 과장의 체계적인 문서화 능력'],
        critical_improvements: ['품질 데이터 전산화 및 실시간 대시보드 구축', '검사 기준서 최신화 및 버전 관리 체계 정립'],
        hiring_needs: ['품질관리팀 품질엔지니어 (경력 2~4년, SQC 경험 우대)'],
      },
      '생산팀': {
        team_composition: `생산팀은 ${emps.length}명으로 팀장 장민수(15년차, 최장 근속)를 중심으로 생산 현장을 관리합니다. 평균 연령 ${avgAge}세, 평균 근속 ${avgTenure}년이며, 신입 구본석(1년차)의 산업안전보건 교육 미이수가 현장 안전 리스크입니다. 연차 사용률이 전사 최저(장민수 팀장 27%)로 번아웃 우려가 있습니다.`,
        capability_gaps: { '스마트팩토리': '생산 설비 IoT 연동 및 MES 시스템 활용 역량 부족', '안전관리': '구본석 사원의 안전교육 미이수로 현장 관리 공백 발생' },
        efficiency_issues: ['장민수 팀장 연차 사용률 27%로 번아웃 고위험', '생산 실적 집계가 수작업으로 이루어져 실시간 파악 곤란', '교대 근무 시 인수인계 체계 미흡으로 품질 편차 발생'],
        team_strengths: ['장민수 팀장의 15년 생산현장 경험과 설비 전문 지식', '홍유리 과장의 공정 최적화 성과 (불량률 전년 대비 8% 감소)', '남기훈 대리의 현장 리더십과 후배 육성'],
        critical_improvements: ['구본석 사원 산업안전보건 교육 즉시 이수 (법적 의무)', '장민수 팀장 연차 사용 독려 및 업무 위임 체계 마련', '생산 데이터 전산화로 실시간 생산성 모니터링 구축'],
        hiring_needs: ['생산팀 생산기술 담당 (경력 3년 이상, 설비 보전 경험)', '생산팀 현장 작업자 (신입, 교대근무 가능)'],
      },
      'IT개발팀': {
        team_composition: `IT개발팀은 ${emps.length}명으로 팀장 하은서(11년차)를 중심으로 사내 시스템 개발·운영을 담당합니다. 평균 연령 ${avgAge}세, 평균 근속 ${avgTenure}년으로 젊은 팀이며, 류재현 과장의 초과근무(주 46시간)와 최신입 백도윤(6개월차)의 법정교육 미이수 4건이 리스크입니다.`,
        capability_gaps: { '보안': '시큐어 코딩 및 취약점 점검 역량 부족으로 보안 사고 대응 제한', 'DevOps/클라우드': 'CI/CD 파이프라인 미성숙, 클라우드 인프라 운영 경험 제한적' },
        efficiency_issues: ['류재현 과장 주간 46시간으로 초과근무 지속', '백도윤 사원 법정교육 4건 미이수 (성희롱예방, 개인정보보호, 산안법, 장애인인식)', '기술 부채 증가로 신규 기능 개발 속도 저하'],
        team_strengths: ['하은서 팀장의 풀스택 개발 역량과 아키텍처 설계 경험', '전승우·노하린 대리의 React/Python 기술 스택 전문성', '사내 HR 시스템 자체 개발로 비용 절감 및 맞춤화 실현'],
        critical_improvements: ['백도윤 사원 법정교육 4건 즉시 이수 (과태료 리스크)', '류재현 과장 업무 부하 조정 및 백도윤 사원에 업무 이관', '보안 취약점 정기 점검 체계 수립'],
        hiring_needs: ['IT개발팀 DevOps 엔지니어 (경력 3년 이상, AWS 경험)', 'IT개발팀 보안 담당 (경력 2년 이상, 시큐어 코딩)'],
      },
    };
    const profile = teamProfiles[team] || {
      team_composition: `${team}은 ${emps.length}명으로 구성되어 있으며, 평균 연령 ${avgAge}세, 평균 근속 ${avgTenure}년입니다.`,
      capability_gaps: {}, efficiency_issues: [], team_strengths: [], critical_improvements: [], hiring_needs: [],
    };
    return { team, total_employees: emps.length, admin_comment: '', analysis: profile };
  },
  updateTeamComment: async () => ({ message: 'Demo mode', team: '', admin_comment: '', reanalyzed: false }),
  getTeamEvaluations: async (team: string) => {
    const teamEvals: Record<string, any[]> = {
      '경영지원팀': [
        { id: 1, evaluation_text: '김도현 부장의 리더십으로 인사·총무·회계 업무가 3명 체제에서도 안정적으로 운영되고 있습니다. 다만 급여 처리 자동화가 시급하며, 박준호 사원의 성장 속도가 기대 이상입니다.', evaluator_name: 'admin', created_at: 1737000000, formatted_date: '2026-01-16' },
        { id: 2, evaluation_text: '이서연 대리의 업무 범위 확대가 필요합니다. 현재 총무 업무 위주에서 인사기획 참여로 역할을 확장하면 팀 역량 강화에 기여할 것으로 판단됩니다.', evaluator_name: 'admin', created_at: 1735700000, formatted_date: '2025-12-31' },
      ],
      '영업팀': [
        { id: 3, evaluation_text: '2025년 매출 목표 달성률 112%로 우수한 성과를 보였습니다. 최민지 팀장의 고객 관리와 정우진 과장의 기술영업 역량이 시너지를 발휘하고 있으나, 정 과장의 초과근무(주 51.5h)에 대한 즉각적 조치가 필요합니다.', evaluator_name: 'admin', created_at: 1737000000, formatted_date: '2026-01-16' },
        { id: 4, evaluation_text: '신입 윤성민·한지수의 법정교육 미이수 건이 HR 감사에서 지적되었습니다. 2월 내 이수 완료를 목표로 교육 일정을 확정했습니다. 강하은 대리의 신규 거래처 개척 성과(3건)는 높이 평가합니다.', evaluator_name: 'admin', created_at: 1734500000, formatted_date: '2025-12-18' },
      ],
      '연구개발팀': [
        { id: 5, evaluation_text: '차세대 제품 프로토타입 개발이 일정대로 진행 중이며, 임태현 팀장의 기술 방향 설정이 적절합니다. 다만 테스트 자동화 커버리지(38%)가 낮아 릴리즈 지연이 반복되고 있어 QA 인력 충원이 시급합니다.', evaluator_name: 'admin', created_at: 1737000000, formatted_date: '2026-01-16' },
        { id: 6, evaluation_text: '오재혁 대리의 품질관리팀→연구개발팀 이동(2024.10) 이후 적응이 잘 이루어지고 있습니다. 품질 관점의 코드 리뷰 참여가 팀 전체 코드 품질 향상에 기여하고 있습니다.', evaluator_name: 'admin', created_at: 1735700000, formatted_date: '2025-12-31' },
        { id: 7, evaluation_text: '신지은 사원(입사 1.5년)의 성장이 눈에 띕니다. Python 기반 분석 도구 개발에 자발적으로 참여하여 팀 생산성에 기여하고 있으며, AI/ML 교육 대상자로 추천합니다.', evaluator_name: 'admin', created_at: 1733200000, formatted_date: '2025-12-03' },
      ],
      '품질관리팀': [
        { id: 8, evaluation_text: '법정교육 이수율 100%를 유지하며 컴플라이언스 모범 팀으로 평가됩니다. 서동훈 팀장의 체계적인 관리가 돋보이며, 유지현 과장의 문서화 역량이 ISO 인증 준비에 큰 도움이 되고 있습니다.', evaluator_name: 'admin', created_at: 1737000000, formatted_date: '2026-01-16' },
        { id: 9, evaluation_text: '품질 데이터 전산화 프로젝트를 IT개발팀과 협업하여 추진 중입니다. 권혁준 대리가 요구사항 정의를 주도하고 있으며, 상반기 내 파일럿 운영을 목표로 합니다.', evaluator_name: 'admin', created_at: 1734500000, formatted_date: '2025-12-18' },
      ],
      '생산팀': [
        { id: 10, evaluation_text: '홍유리 과장의 공정 최적화 성과로 불량률이 전년 대비 8% 감소했습니다. 장민수 팀장의 현장 경험(15년)이 팀 안정성의 핵심이나, 연차 사용률 27%로 번아웃 리스크가 우려됩니다. 하반기 2주 이상 연속 휴가 사용을 권고합니다.', evaluator_name: 'admin', created_at: 1737000000, formatted_date: '2026-01-16' },
        { id: 11, evaluation_text: '신입 구본석(입사 1년)의 산업안전보건 교육 미이수가 확인되었습니다. 생산 현장 근무자의 안전교육은 법적 의무이므로 즉시 이수 조치가 필요합니다. 남기훈 대리의 멘토링으로 현장 적응은 양호합니다.', evaluator_name: 'admin', created_at: 1735700000, formatted_date: '2025-12-31' },
      ],
      'IT개발팀': [
        { id: 12, evaluation_text: '사내 HR 시스템 자체 개발 프로젝트가 성공적으로 진행 중입니다. 하은서 팀장의 아키텍처 설계와 전승우·노하린 대리의 구현 역량이 우수하며, 외주 대비 약 40% 비용 절감 효과가 있습니다.', evaluator_name: 'admin', created_at: 1737000000, formatted_date: '2026-01-16' },
        { id: 13, evaluation_text: '백도윤 사원(입사 6개월)의 법정교육 4건 미이수는 온보딩 프로세스 개선이 필요함을 시사합니다. 류재현 과장의 초과근무(주 46h)도 백도윤 사원의 역량 성장에 맞춰 점진적으로 업무 이관이 필요합니다.', evaluator_name: 'admin', created_at: 1735700000, formatted_date: '2025-12-31' },
        { id: 14, evaluation_text: '양세은 사원의 프론트엔드 개발 역량이 빠르게 성장하고 있습니다. 최근 대시보드 리디자인 프로젝트에서 주도적 역할을 수행했으며, 코드 품질도 양호합니다.', evaluator_name: 'admin', created_at: 1733200000, formatted_date: '2025-12-03' },
      ],
    };
    const evals = teamEvals[team] || [];
    return { team_name: team, evaluations: evals, total: evals.length };
  },
  addTeamEvaluation: async () => ({ message: 'Demo mode', evaluation_id: 0, reanalyzed: false }),
  deleteTeamEvaluation: async () => ({ message: 'Demo mode', deleted_id: 0 }),

  // ========== Statistics & Insights ==========
  getStatistics: async () => demoData.statistics ?? {
    total_employees: 0, total_teams: 0, average_age: 0,
    team_distribution: {}, position_distribution: {},
    age_distribution: { min: 0, max: 0, ages: [] },
  },
  getInsights: async () => demoData.insights ?? {
    summary: '', structural_issues: [], skill_gaps: [],
    risk_points: [], immediate_actions: [], hiring_strategy: [], cost_efficiency: '',
  },
  getRegionDistribution: async () => demoData.regionDistribution ?? {
    region_distribution: {}, total_with_address: 0, total_without_address: 0, total_employees: 0,
  },
  getExecutiveStats: async () => ({
    success: true,
    data: {
      compliance: { overtime_critical: 1, overtime_high: 3, training_overdue: 5, contracts_undelivered: 2, contracts_expiring: 4, termination_risks: 0 },
      salary: { by_rank: [
        { rank_name: '사원', base_salary: 35000000, min_salary: 30000000, max_salary: 40000000, employee_count: 8 },
        { rank_name: '대리', base_salary: 42000000, min_salary: 38000000, max_salary: 50000000, employee_count: 6 },
        { rank_name: '과장', base_salary: 52000000, min_salary: 46000000, max_salary: 60000000, employee_count: 5 },
        { rank_name: '차장', base_salary: 62000000, min_salary: 55000000, max_salary: 72000000, employee_count: 3 },
        { rank_name: '부장', base_salary: 75000000, min_salary: 65000000, max_salary: 88000000, employee_count: 2 },
      ] },
      salary_analysis: {
        by_rank: [
          { rank_name: '사원', employee_count: 8, avg_salary: 36500000, actual_min: 32000000, actual_max: 41000000, table_base: 35000000, table_min: 30000000, table_max: 40000000, within_range: 7, below_min: 0, above_max: 1, status: 'warning' },
          { rank_name: '대리', employee_count: 6, avg_salary: 44000000, actual_min: 39000000, actual_max: 49000000, table_base: 42000000, table_min: 38000000, table_max: 50000000, within_range: 6, below_min: 0, above_max: 0, status: 'normal' },
          { rank_name: '과장', employee_count: 5, avg_salary: 53500000, actual_min: 44000000, actual_max: 59000000, table_base: 52000000, table_min: 46000000, table_max: 60000000, within_range: 4, below_min: 1, above_max: 0, status: 'danger' },
          { rank_name: '차장', employee_count: 3, avg_salary: 64000000, actual_min: 57000000, actual_max: 71000000, table_base: 62000000, table_min: 55000000, table_max: 72000000, within_range: 3, below_min: 0, above_max: 0, status: 'normal' },
          { rank_name: '부장', employee_count: 2, avg_salary: 78000000, actual_min: 68000000, actual_max: 89000000, table_base: 75000000, table_min: 65000000, table_max: 88000000, within_range: 1, below_min: 0, above_max: 1, status: 'warning' },
        ],
        salary_issues: [
          { name: '이영희', team: '연구개발팀', rank: '과장', salary: 44000000, gap: 2000000, type: 'below_min', eval_grade: 'B', message: '최소 기준 대비 2,000,000원 미달' },
          { name: '박민수', team: '영업팀', rank: '부장', salary: 89000000, gap: 1000000, type: 'above_max', eval_grade: 'A', message: '최대 기준 대비 1,000,000원 초과' },
        ],
        eval_issues: [
          { name: '최지훈', team: 'IT개발팀', rank: '대리', salary: 39000000, type: 'underpaid_high_grade', eval_grade: 'S', message: '평가 S등급이나 급여 하위 — 인상 검토' },
          { name: '한승우', team: '경영지원팀', rank: '사원', salary: 38000000, type: 'no_evaluation', message: '평가 자료 부족 — 평가 점수 기입 필요' },
          { name: '김태호', team: '영업팀', rank: '대리', salary: 45000000, type: 'no_interview', eval_grade: 'B', message: '면담 미실시 — 면담 기록 입력 필요' },
        ],
        summary: { total_analyzed: 24, salary_issue_count: 2, no_eval_count: 5, no_interview_count: 3, compliance_rate: 91.7 },
        recommendations: ['급여 테이블 최소 미달 1명 — 급여 인상 검토 필요', '급여 테이블 최대 초과 1명 — 승진 또는 테이블 조정 검토', '고성과(S/A) 저급여 1명 — 성과 반영 인상 검토', '평가 미완료 5명 — 평가 점수 기입 필요', '면담 미실시 3명 — 면담 기록 입력 필요'],
      },
      headcount: { year: 2026, total_planned: 40, total_current: 37, total_vacancy: 3, fill_rate: 92.5, departments: [
        { department: '개발본부', planned_count: 15, current_count: 14, vacancy: 1, surplus: 0 },
        { department: '경영지원팀', planned_count: 8, current_count: 8, vacancy: 0, surplus: 0 },
        { department: '디자인팀', planned_count: 5, current_count: 4, vacancy: 1, surplus: 0 },
        { department: '영업팀', planned_count: 7, current_count: 6, vacancy: 1, surplus: 0 },
        { department: '연구소', planned_count: 5, current_count: 5, vacancy: 0, surplus: 0 },
      ] },
    },
  }),
  clearCache: async () => ({ success: true, message: 'Cache cleared (demo)' }),

  // ========== Cohort Analysis ==========
  getCohortAnalysis: async () => {
    const realisticCohorts = [
      {
        cohort_id: '2019', join_count: 8, current_count: 5, retention_rate: 62.5,
        promoted_count: 3, promotion_rate: 37.5, resigned_count: 3, attrition_rate: 37.5,
        avg_tenure_months: 72,
        members: [
          { employee_id: 'D01', name: '김태영', status: 'active' }, { employee_id: 'D02', name: '박지훈', status: 'active' },
          { employee_id: 'D03', name: '이수민', status: 'resigned' }, { employee_id: 'D04', name: '최동욱', status: 'active' },
          { employee_id: 'D05', name: '정하늘', status: 'resigned' }, { employee_id: 'D06', name: '한수진', status: 'active' },
          { employee_id: 'D07', name: '오현석', status: 'resigned' }, { employee_id: 'D08', name: '임서윤', status: 'active' },
        ],
      },
      {
        cohort_id: '2020', join_count: 6, current_count: 5, retention_rate: 83.3,
        promoted_count: 2, promotion_rate: 33.3, resigned_count: 1, attrition_rate: 16.7,
        avg_tenure_months: 60,
        members: [
          { employee_id: 'D11', name: '강민준', status: 'active' }, { employee_id: 'D12', name: '윤서연', status: 'active' },
          { employee_id: 'D13', name: '조은비', status: 'resigned' }, { employee_id: 'D14', name: '장우진', status: 'active' },
          { employee_id: 'D15', name: '신혜린', status: 'active' }, { employee_id: 'D16', name: '류지호', status: 'active' },
        ],
      },
      {
        cohort_id: '2021', join_count: 10, current_count: 7, retention_rate: 70.0,
        promoted_count: 2, promotion_rate: 20.0, resigned_count: 3, attrition_rate: 30.0,
        avg_tenure_months: 48,
        members: [
          { employee_id: 'D21', name: '배준혁', status: 'active' }, { employee_id: 'D22', name: '문가영', status: 'resigned' },
          { employee_id: 'D23', name: '송민수', status: 'active' }, { employee_id: 'D24', name: '황예진', status: 'active' },
          { employee_id: 'D25', name: '노승우', status: 'resigned' }, { employee_id: 'D26', name: '전다은', status: 'active' },
          { employee_id: 'D27', name: '고재현', status: 'active' }, { employee_id: 'D28', name: '안소라', status: 'resigned' },
          { employee_id: 'D29', name: '서동현', status: 'active' }, { employee_id: 'D30', name: '권수빈', status: 'active' },
        ],
      },
      {
        cohort_id: '2022', join_count: 12, current_count: 10, retention_rate: 83.3,
        promoted_count: 1, promotion_rate: 8.3, resigned_count: 2, attrition_rate: 16.7,
        avg_tenure_months: 36,
        members: [
          { employee_id: 'D31', name: '이현우', status: 'active' }, { employee_id: 'D32', name: '박소영', status: 'active' },
          { employee_id: 'D33', name: '김도윤', status: 'resigned' }, { employee_id: 'D34', name: '정민아', status: 'active' },
          { employee_id: 'D35', name: '최성진', status: 'active' }, { employee_id: 'D36', name: '한지우', status: 'active' },
          { employee_id: 'D37', name: '유재윤', status: 'active' }, { employee_id: 'D38', name: '임하나', status: 'resigned' },
          { employee_id: 'D39', name: '오영진', status: 'active' }, { employee_id: 'D40', name: '강다현', status: 'active' },
          { employee_id: 'D41', name: '윤태호', status: 'active' }, { employee_id: 'D42', name: '조미래', status: 'active' },
        ],
      },
      {
        cohort_id: '2023', join_count: 7, current_count: 6, retention_rate: 85.7,
        promoted_count: 0, promotion_rate: 0, resigned_count: 1, attrition_rate: 14.3,
        avg_tenure_months: 24,
        members: [
          { employee_id: 'D51', name: '장서준', status: 'active' }, { employee_id: 'D52', name: '신유나', status: 'active' },
          { employee_id: 'D53', name: '류현서', status: 'active' }, { employee_id: 'D54', name: '배아린', status: 'resigned' },
          { employee_id: 'D55', name: '문재혁', status: 'active' }, { employee_id: 'D56', name: '황지민', status: 'active' },
          { employee_id: 'D57', name: '노예슬', status: 'active' },
        ],
      },
      {
        cohort_id: '2024', join_count: 5, current_count: 5, retention_rate: 100.0,
        promoted_count: 0, promotion_rate: 0, resigned_count: 0, attrition_rate: 0,
        avg_tenure_months: 12,
        members: [
          { employee_id: 'D61', name: '전수호', status: 'active' }, { employee_id: 'D62', name: '고하은', status: 'active' },
          { employee_id: 'D63', name: '안태윤', status: 'active' }, { employee_id: 'D64', name: '서지원', status: 'probation' },
          { employee_id: 'D65', name: '권도영', status: 'probation' },
        ],
      },
    ];
    const totalJoin = realisticCohorts.reduce((s, c) => s + c.join_count, 0);
    const totalActive = realisticCohorts.reduce((s, c) => s + c.current_count, 0);
    return {
      group_by: 'year',
      cohorts: realisticCohorts,
      summary: {
        total_cohorts: realisticCohorts.length,
        overall_retention: Math.round(totalActive / totalJoin * 1000) / 10,
        best_cohort: '2024',
        worst_cohort: '2019',
      },
    };
  },

  // ========== DEI Analysis ==========
  getDEIAnalysis: async () => {
    const employees = demoData.employees?.employees || [];
    const total = employees.length;
    const genderMap: Record<string, number> = {};
    const ageMap: Record<string, number> = {};
    const regionMap: Record<string, number> = {};
    const teamGender: Record<string, { male: number; female: number }> = {};
    const posGender: Record<string, { male: number; female: number }> = {};
    employees.forEach((e: any) => {
      const g = e.gender === 'male' ? '남성' : e.gender === 'female' ? '여성' : '기타';
      genderMap[g] = (genderMap[g] || 0) + 1;
      const age = e.age || 30;
      const ag = age < 30 ? '20대' : age < 40 ? '30대' : age < 50 ? '40대' : '50대 이상';
      ageMap[ag] = (ageMap[ag] || 0) + 1;
      const reg = (e.region || '').includes('서울') ? '서울' : (e.region || '').includes('경기') ? '경기' : (e.region || '').includes('인천') ? '인천' : '기타';
      regionMap[reg] = (regionMap[reg] || 0) + 1;
      const gKey = e.gender === 'male' ? 'male' : 'female';
      if (e.team) {
        if (!teamGender[e.team]) teamGender[e.team] = { male: 0, female: 0 };
        teamGender[e.team][gKey]++;
      }
      if (e.position) {
        if (!posGender[e.position]) posGender[e.position] = { male: 0, female: 0 };
        posGender[e.position][gKey]++;
      }
    });
    const makeDist = (m: Record<string, number>) => Object.entries(m).sort((a, b) => b[1] - a[1]).map(([k, v]) => ({ category: k, count: v, ratio: Math.round(v / total * 1000) / 10 }));
    const makeGD = (m: Record<string, { male: number; female: number }>) => Object.entries(m).sort().map(([k, v]) => ({ team: k, male: v.male, female: v.female, total: v.male + v.female, female_ratio: Math.round(v.female / (v.male + v.female) * 1000) / 10 }));
    const maleCount = genderMap['남성'] || 0;
    const femaleCount = genderMap['여성'] || 0;
    const gTotal = maleCount + femaleCount;
    const maleRatio = gTotal > 0 ? maleCount / gTotal * 100 : 50;
    const ageValues = Object.values(ageMap);
    const ageTotal = ageValues.reduce((s, n) => s + n, 0);
    const simpson = ageTotal > 1 ? 1 - ageValues.reduce((s, n) => s + n * (n - 1), 0) / (ageTotal * (ageTotal - 1)) : 0;
    return {
      gender_distribution: makeDist(genderMap),
      age_distribution: makeDist(ageMap),
      region_distribution: makeDist(regionMap),
      team_gender: makeGD(teamGender),
      position_gender: makeGD(posGender),
      summary: {
        total_employees: total,
        gender_balance_score: Math.round((100 - Math.abs(maleRatio - 50) * 2) * 10) / 10,
        age_diversity_index: Math.round(simpson * 100) / 100,
        region_count: Object.keys(regionMap).length,
      },
    };
  },

  // ========== AI Analysis ==========
  getCohortAIAnalysis: async () => ({
    statistics: { group_by: 'year', cohorts: [], summary: { total_cohorts: 3, overall_retention: 85.0, best_cohort: '2023', worst_cohort: '2021' } },
    ai_analysis: { summary: '전반적으로 안정적인 리텐션을 보이고 있으며, 최근 입사자 코호트의 정착률이 높습니다.', retention_insights: ['2023년 코호트의 리텐션이 가장 우수합니다', '초기 6개월 내 이탈 패턴에 주의가 필요합니다'], risk_cohorts: ['2021년 코호트는 이직률이 상대적으로 높습니다'], recommendations: ['온보딩 프로그램 강화로 초기 이탈 방지', '핵심 인재 리텐션 프로그램 운영'] },
  }),
  getDEIAIAnalysis: async () => ({
    statistics: { gender_distribution: [], age_distribution: [], summary: { total_employees: 37 } },
    ai_analysis: { summary: '조직의 다양성 지수는 양호한 수준이나, 일부 직군에서 성별 균형 개선이 필요합니다.', diversity_insights: ['연령대 다양성 지수가 양호합니다', '기술직에서 여성 비율 개선 필요'], improvement_areas: ['여성 기술 인력 채용 확대', '연령대별 균형 있는 팀 구성'], legal_note: '본 분석은 참고용이며 인사 결정의 직접적 근거로 사용할 수 없습니다.' },
  }),
  getOrgEfficiencyAIAnalysis: async () => ({
    statistics: { summary: { overall_score: 72 } },
    ai_analysis: { summary: '조직 효율성은 보통 수준으로, Span of Control 최적화를 통해 개선 가능합니다.', structural_insights: ['관리자 1인당 직속 보고자 수가 IT 업계 평균보다 낮습니다', '직급 피라미드는 정상적인 형태를 보입니다'], optimization_areas: ['팀 규모 적정성 재검토', '중간관리자 역할 재정의'], benchmarks: 'IT R&D 업계 평균 대비 조직 규모는 소형이나, 팀당 인원은 적정 범위 내에 있습니다.' },
  }),
  getPersonnelTrends: async () => ({
    months: [
      { month: '2025-07', joins: 2, resigns: 0, transfers: 1, net: 2 },
      { month: '2025-08', joins: 1, resigns: 1, transfers: 0, net: 0 },
      { month: '2025-09', joins: 0, resigns: 0, transfers: 2, net: 0 },
      { month: '2025-10', joins: 3, resigns: 1, transfers: 0, net: 2 },
      { month: '2025-11', joins: 1, resigns: 0, transfers: 1, net: 1 },
      { month: '2025-12', joins: 0, resigns: 1, transfers: 0, net: -1 },
      { month: '2026-01', joins: 2, resigns: 0, transfers: 0, net: 2 },
      { month: '2026-02', joins: 1, resigns: 0, transfers: 1, net: 1 },
    ],
  }),
};
